import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, FolderOpen, LayoutGrid, List, Plus } from 'lucide-react';
import { projectService, type Project } from '../services/projectService';
import { subProjectService, type SubProject } from '../services/subProjectService';
import { useCanCreateProjects } from '../hooks/usePermissions';
import PageHeader from '@/components/layout/PageHeader';
import SearchInput from '@/components/shared/SearchInput';
import EmptyState from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const fmt = (d: string) => new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

type ViewType = 'grid' | 'table';
type SortType = 'modified' | 'created' | 'name';

const Projects: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const canCreate = useCanCreateProjects();
  const [projects, setProjects] = useState<Project[]>([]);
  const [allSubProjects, setAllSubProjects] = useState<SubProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [subProjectsLoading, setSubProjectsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortType>('modified');
  const [view, setView] = useState<ViewType>('grid');
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', head_of_project: '' });
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  const [expandedTableRows, setExpandedTableRows] = useState<Set<string>>(new Set());

  useEffect(() => { loadProjects(); }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setSubProjectsLoading(true);
      const [fetchedProjects, fetchedSubProjects] = await Promise.all([
        projectService.getProjects(),
        subProjectService.getAll(),
      ]);
      setProjects(fetchedProjects);
      setAllSubProjects(fetchedSubProjects);
    } catch {
      // error handled by API layer
    } finally {
      setLoading(false);
      setSubProjectsLoading(false);
    }
  };

  const subProjectsByProjectId = useMemo(() => {
    const map: Record<string, SubProject[]> = {};
    for (const sp of allSubProjects) {
      if (sp.status !== 'defined') continue;
      if (!map[sp.project_id]) map[sp.project_id] = [];
      map[sp.project_id].push(sp);
    }
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime());
    }
    return map;
  }, [allSubProjects]);

  const filtered = React.useMemo(() => {
    let list = [...projects];
    if (search) list = list.filter(p =>
      p.name.toLowerCase().includes(search.toLowerCase()) ||
      p.description?.toLowerCase().includes(search.toLowerCase())
    );
    list.sort((a, b) => {
      if (sort === 'name') return a.name.localeCompare(b.name);
      if (sort === 'created') return new Date(b.created_date).getTime() - new Date(a.created_date).getTime();
      return new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime();
    });
    return list;
  }, [projects, search, sort]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setCreating(true);
      await projectService.createProject({
        name: form.name,
        description: form.description || undefined,
        head_of_project: form.head_of_project || undefined,
      });
      setDialogOpen(false);
      setForm({ name: '', description: '', head_of_project: '' });
      loadProjects();
    } catch {
      alert('Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const go = useCallback((id: string) => navigate(`/project/${id}`), [navigate]);

  const emptyAction = canCreate && projects.length === 0
    ? { label: t('pages.projects.newProject'), onClick: () => setDialogOpen(true) }
    : undefined;

  return (
    <>
      <PageHeader
        title={t('pages.projects.title')}
        subtitle={loading ? '' : `${filtered.length} project${filtered.length !== 1 ? 's' : ''}`}
        actions={canCreate ? (
          <Button size="sm" onClick={() => setDialogOpen(true)}>
            <Plus size={14} className="mr-1" />{t('pages.projects.newProject')}
          </Button>
        ) : undefined}
      />

      {/* Toolbar */}
      <div className="flex items-center gap-2 px-6 pt-4 pb-2">
        <SearchInput value={search} onChange={setSearch} placeholder="Search projects…" className="w-56" />
        <Select value={sort} onValueChange={v => setSort(v as SortType)}>
          <SelectTrigger className="h-8 w-36 text-xs border-slate-200"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="modified">Last modified</SelectItem>
            <SelectItem value="created">Date created</SelectItem>
            <SelectItem value="name">Name A–Z</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-1">
          <Button size="icon" variant={view === 'grid' ? 'secondary' : 'ghost'} className="h-7 w-7" onClick={() => setView('grid')}><LayoutGrid size={14} /></Button>
          <Button size="icon" variant={view === 'table' ? 'secondary' : 'ghost'} className="h-7 w-7" onClick={() => setView('table')}><List size={14} /></Button>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 pb-6">
        {loading ? (
          view === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-4 space-y-2">
                  <Skeleton className="h-4 w-2/3" /><Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-4/5" />
                  <div className="pt-1.5 space-y-1.5 border-t border-slate-100">
                    <Skeleton className="h-3 w-full" /><Skeleton className="h-3 w-5/6" /><Skeleton className="h-3 w-4/6" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-md border border-slate-200 mt-2 overflow-hidden">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex gap-6 px-4 py-2.5 border-b border-slate-100 last:border-0">
                  <Skeleton className="h-4 w-40" /><Skeleton className="h-4 w-24" /><Skeleton className="h-4 w-32" /><Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          )
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={FolderOpen}
            title={projects.length === 0 ? t('pages.projects.noProjects') : 'No matching projects'}
            description={projects.length === 0 ? t('pages.projects.createFirst') : 'Try a different search term'}
            action={emptyAction}
          />
        ) : view === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-2">
            {filtered.map(p => {
              const spList = subProjectsByProjectId[p.id] ?? [];
              const isExpanded = expandedCards.has(p.id);
              const shown = isExpanded ? spList : spList.slice(0, 5);
              const hidden = spList.length - 5;
              return (
                <div
                  key={p.id}
                  onClick={() => go(p.id)}
                  className="rounded-lg border border-slate-200 bg-white p-4 cursor-pointer hover:border-slate-400 hover:shadow-sm transition-all flex flex-col gap-1.5"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-sm font-medium text-slate-800 truncate">{p.name}</h3>
                    <Badge variant="outline" className="text-[10px] shrink-0">{spList.length} active</Badge>
                  </div>
                  {p.description && <p className="text-xs text-slate-500 line-clamp-2">{p.description}</p>}

                  {/* Sub-projects section */}
                  <div className="border-t border-slate-100 mt-1 pt-1.5 flex flex-col gap-0.5">
                    {subProjectsLoading ? (
                      <>
                        <Skeleton className="h-3 w-full" />
                        <Skeleton className="h-3 w-5/6" />
                        <Skeleton className="h-3 w-4/6" />
                      </>
                    ) : spList.length === 0 ? (
                      <span className="text-[11px] text-slate-400 italic">No active sub-projects</span>
                    ) : (
                      <>
                        {shown.map(sp => (
                          <div
                            key={sp.id}
                            onClick={e => { e.stopPropagation(); navigate(`/sub-project/${sp.id}`); }}
                            className="flex items-center gap-1 py-0.5 text-xs text-slate-600 hover:text-slate-900 cursor-pointer group"
                          >
                            <ChevronRight size={11} className="shrink-0 text-slate-400 group-hover:text-slate-600" />
                            <span className="truncate">{sp.name}</span>
                          </div>
                        ))}
                        {spList.length > 5 && (
                          <button
                            onClick={e => {
                              e.stopPropagation();
                              setExpandedCards(prev => {
                                const next = new Set(prev);
                                if (isExpanded) next.delete(p.id); else next.add(p.id);
                                return next;
                              });
                            }}
                            className="text-[11px] text-blue-500 hover:text-blue-700 text-left mt-0.5"
                          >
                            {isExpanded ? 'Show less' : `+${hidden} more`}
                          </button>
                        )}
                      </>
                    )}
                  </div>

                  <div className="flex items-center justify-between text-[11px] text-slate-400 pt-1.5 border-t border-slate-100 mt-auto">
                    <span>{p.head_of_project || '—'}</span>
                    <span>{fmt(p.last_updated)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-md border border-slate-200 mt-2 overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="text-xs font-medium text-slate-500 h-8 py-0 w-[35%]">Name</TableHead>
                  <TableHead className="text-xs font-medium text-slate-500 h-8 py-0">Head</TableHead>
                  <TableHead className="text-xs font-medium text-slate-500 h-8 py-0 w-[35%]">Active sub-projects</TableHead>
                  <TableHead className="text-xs font-medium text-slate-500 h-8 py-0">Modified</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(p => {
                  const spList = subProjectsByProjectId[p.id] ?? [];
                  const isExpanded = expandedTableRows.has(p.id);
                  const shown = isExpanded ? spList : spList.slice(0, 3);
                  const hidden = spList.length - 3;
                  return (
                    <TableRow key={p.id} className="cursor-pointer hover:bg-slate-50" onClick={() => go(p.id)}>
                      <TableCell className="py-2 text-sm font-medium text-slate-800">{p.name}</TableCell>
                      <TableCell className="py-2 text-sm text-slate-600">{p.head_of_project || '—'}</TableCell>
                      <TableCell className="py-2 text-sm text-slate-600">
                        {subProjectsLoading ? (
                          <Skeleton className="h-3 w-24" />
                        ) : spList.length === 0 ? (
                          <span className="text-[11px] text-slate-400 italic">—</span>
                        ) : (
                          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                            {shown.map((sp, idx) => (
                              <React.Fragment key={sp.id}>
                                <span
                                  onClick={e => { e.stopPropagation(); navigate(`/sub-project/${sp.id}`); }}
                                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                                >{sp.name}</span>
                                {idx < shown.length - 1 && <span className="text-slate-300 text-xs">·</span>}
                              </React.Fragment>
                            ))}
                            {spList.length > 3 && (
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  setExpandedTableRows(prev => {
                                    const next = new Set(prev);
                                    if (isExpanded) next.delete(p.id); else next.add(p.id);
                                    return next;
                                  });
                                }}
                                className="text-[11px] text-blue-500 hover:text-blue-700"
                              >
                                {isExpanded ? 'less' : `+${hidden} more`}
                              </button>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-2 text-sm text-slate-500">{fmt(p.last_updated)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('pages.projects.create_modal_title')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-3 pt-1">
            <div className="space-y-1">
              <Label>{t('pages.projects.project_name')} *</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required maxLength={200} placeholder="e.g. EV Battery Cooling" />
            </div>
            <div className="space-y-1">
              <Label>{t('pages.projects.description')}</Label>
              <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} maxLength={1000} placeholder="Short product description" className="resize-none" />
            </div>
            <div className="space-y-1">
              <Label>{t('pages.projects.head_of_project')}</Label>
              <Input value={form.head_of_project} onChange={e => setForm(f => ({ ...f, head_of_project: e.target.value }))} placeholder="Engineer name" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('pages.projects.cancel')}</Button>
              <Button type="submit" disabled={creating}>{creating ? t('pages.projects.creating') : t('pages.projects.create')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Projects;
